import { Base, ReverCard } from '@itsrever/design-system'
import { useTranslation } from 'react-i18next'
import {
  BrandInformation as BrandInformationInfo,
  OnboardingAction,
  OnboardingPlatform
} from '@/domain/onboarding'
import React from 'react'
import { isValidIdentifier } from '@/utils'
import { CheckboxOption } from '../checkbox-option'

const { FieldRoot, FieldLabel, FieldDescription, FieldError, Input } = Base

// The platforms offered during onboarding. The element type constrains entries
// TO the union, so a typo or a value removed from the union fails to compile —
// it does NOT enforce that every member appears, and should not: a member may be
// deliberately unoffered. Exhaustiveness is enforced downstream instead, by
// toBackendPlatform's `never` guard, which is where an unmapped platform would
// actually do damage.
//
// It used to be a bare string array inline in the JSX, connected to the union in
// neither direction.
const ONBOARDING_PLATFORM_OPTIONS: Exclude<OnboardingPlatform, undefined>[] = [
  'Shopify',
  'Shopify Plus',
  'WooCommerce',
  'Prestashop',
  'Magento',
  'Shopware',
  'Commerce Layer',
  'Salesforce',
  'LogiCommerce',
  'TikTok Shop',
  'Custom platform',
  'Custom platform v2',
  'Inditex'
]

interface BrandInformationProps {
  brandInformation: BrandInformationInfo
  dispatch: React.Dispatch<OnboardingAction>
}

export const BrandInformation: React.FC<BrandInformationProps> = ({
  brandInformation,
  dispatch
}) => {
  const { t } = useTranslation()

  const handleChangePlatform = (platform: BrandInformationInfo['platform']) => {
    if (
      platform !== 'Shopify' &&
      platform !== 'Shopify Plus' &&
      platform !== 'TikTok Shop' &&
      platform !== 'Prestashop'
    )
      dispatch({
        type: 'UPDATE_BRAND_INFORMATION',
        payload: {
          ...brandInformation,
          platform,
          restock: undefined,
          shopifyCollaboratorAccess: undefined,
          tiktokShopId: undefined
        }
      })
    else
      dispatch({
        type: 'UPDATE_BRAND_INFORMATION',
        payload: {
          ...brandInformation,
          platform
        }
      })
  }

  const handleClickRestock = (restock: boolean) => {
    dispatch({
      type: 'UPDATE_BRAND_INFORMATION',
      payload: {
        ...brandInformation,
        restock
      }
    })
  }

  const handleClickContracts = (
    contractsToUse: BrandInformationInfo['contractsToUse']
  ) => {
    dispatch({
      type: 'UPDATE_BRAND_INFORMATION',
      payload: {
        ...brandInformation,
        contractsToUse
      }
    })
  }

  const canRestock =
    brandInformation.platform === 'Shopify' ||
    brandInformation.platform === 'Shopify Plus' ||
    brandInformation.platform === 'Prestashop'

  const errorIdentifier = (identifier: string): boolean => {
    return Boolean(identifier && !isValidIdentifier(identifier))
  }
  const helperTextIdentifier = (identifier: string) => {
    if (identifier && !isValidIdentifier(identifier))
      return 'Special characters are not allowed'
    return ''
  }

  return (
    <ReverCard className="p-4" fullWidth>
      <div className="mb-4 flex items-center gap-1">
        <h6>{t('onboarding.brand_information')}</h6>
      </div>
      <div className="flex flex-col gap-4">
        <FieldRoot invalid={errorIdentifier(brandInformation.ecommerceName)}>
          <FieldLabel>{t('onboarding.ecommerce')}</FieldLabel>
          <Input
            placeholder="Nike Inc."
            value={brandInformation.ecommerceName}
            onChange={(e) =>
              dispatch({
                type: 'UPDATE_BRAND_INFORMATION',
                payload: {
                  ...brandInformation,
                  ecommerceName: e.currentTarget.value
                }
              })
            }
          />
          {errorIdentifier(brandInformation.ecommerceName) ? (
            <FieldError match>
              {helperTextIdentifier(brandInformation.ecommerceName)}
            </FieldError>
          ) : null}
        </FieldRoot>
        {brandInformation.group ? (
          <FieldRoot invalid={errorIdentifier(brandInformation.group)}>
            <FieldLabel>{t('onboarding.group')}</FieldLabel>
            <Input
              placeholder="Inditex Group"
              value={brandInformation.group}
              disabled
            />
            {errorIdentifier(brandInformation.group) ? (
              <FieldError match>
                {helperTextIdentifier(brandInformation.group)}
              </FieldError>
            ) : null}
          </FieldRoot>
        ) : null}
      </div>
      <p className="mb-2 mt-4 font-semibold">
        {t('onboarding.platform')}
        <span className="text-error-main"> *</span>
      </p>
      {ONBOARDING_PLATFORM_OPTIONS.map((platform) => (
        <CheckboxOption
          key={platform}
          label={platform}
          checked={brandInformation.platform === platform}
          onCheckedChange={() => handleChangePlatform(platform)}
        />
      ))}

      {['Shopify', 'Shopify Plus'].includes(
        brandInformation.platform ?? ''
      ) && (
        <FieldRoot className="mt-4">
          <FieldLabel>{t('onboarding.shopify_colaborator')}</FieldLabel>
          <Input
            placeholder={t('onboarding.shopify_colaborator_description')}
            value={brandInformation.shopifyCollaboratorAccess || ''}
            onChange={(e) => {
              dispatch({
                type: 'UPDATE_BRAND_INFORMATION',
                payload: {
                  ...brandInformation,
                  shopifyCollaboratorAccess: e.currentTarget.value
                }
              })
            }}
          />
          <FieldDescription>
            {brandInformation.platform === 'Shopify'
              ? t('onboarding.shopify_colaborator_instructions')
              : t('onboarding.shopifyplus_colaborator_instructions')}
          </FieldDescription>
        </FieldRoot>
      )}

      {brandInformation.platform === 'TikTok Shop' && (
        <FieldRoot className="mt-4">
          <FieldLabel>{t('onboarding.tiktok_shop_id')}</FieldLabel>
          <Input
            placeholder={t('onboarding.tiktok_shop_id_description')}
            value={brandInformation.tiktokShopId ?? ''}
            onChange={(e) => {
              dispatch({
                type: 'UPDATE_BRAND_INFORMATION',
                payload: {
                  ...brandInformation,
                  tiktokShopId: e.currentTarget.value
                }
              })
            }}
          />
          <FieldDescription>
            {t('onboarding.tiktok_shop_id_instructions')}
          </FieldDescription>
        </FieldRoot>
      )}

      {canRestock ? (
        <>
          <p className="mt-4 font-semibold">
            {t('onboarding.restock_from_dashboard')}
            <span className="text-error-main"> *</span>
          </p>
          <p className="mb-2 text-xs text-grey-3">
            {t('onboarding.restock_from_dashboard_description')}
          </p>
          <CheckboxOption
            label={t('onboarding.yes')}
            checked={brandInformation.restock === true}
            onCheckedChange={() => handleClickRestock(true)}
          />
          <CheckboxOption
            label={t('onboarding.no')}
            checked={brandInformation.restock === false}
            onCheckedChange={() => handleClickRestock(false)}
          />
        </>
      ) : null}
      <p className="mb-2 mt-4 font-semibold">
        {t('onboarding.contracts_to_use')}
        <span className="text-error-main"> *</span>
      </p>
      <CheckboxOption
        label={t('onboarding.rever_contracts')}
        checked={brandInformation.contractsToUse === 'rever'}
        onCheckedChange={() => handleClickContracts('rever')}
      />
      <CheckboxOption
        label={t('onboarding.own_contracts')}
        checked={brandInformation.contractsToUse === 'own'}
        onCheckedChange={() => handleClickContracts('own')}
      />
    </ReverCard>
  )
}
